import { Duration } from "luxon"
import React, { useEffect } from "react"
import { useSelector } from "react-redux"
import { Logger } from "shared/Helpers/logging"
import { useInterval } from "shared/hooks/useInterval"
import { useEnvironment } from "shared/Modules/Environment/envHooks"
import { ErrorDisplay } from "shared/Modules/Error/errorTypes"
import { useToken } from "shared/Modules/Login/useToken"
import { selectConfigProperties, selectUserPromptConfig } from "shared/Modules/Properties/propertySelectors"
import { useQueryWithOptions } from "shared/Modules/Query/useQuery"
import { ModalUserPrompt } from "../Screens/ModalUserPrompt"
import { getUserPrompts } from "../userAPI"
import { isSettled } from "shared/Modules/Cordova/cordovaLib"
import { useCordova } from "shared/Modules/Cordova/hooks/useCordova"
import { usePushPermissionPrompt } from "../pushPermissionPrompt"
import { selectAcceptPushNotifications } from "../userSelectors"
import { UserPromptType } from "../userTypes"
import { useUserPrompts } from "./UserPromptProvider"

type UserPromptsProps = Readonly<{
    page: string
}>

export function UserPrompts({ page }: UserPromptsProps) {
    const { refreshContentInMinutes } = useSelector(selectConfigProperties)
    const { pollIntervalInMinutes = refreshContentInMinutes } = useSelector(selectUserPromptConfig) ?? {}

    const token = useToken()
    const { currentEnv } = useEnvironment()

    // TEMPORARY (task 328): trigger the notification permission prompt at startup as if the API had
    // returned it (nothing comes from the API for this yet). Only on the startup page, only once Cordova
    // has settled (so Firebase is available when the user presses the button) and not once the user has
    // already granted permission (ACCEPT_PUSH_NOTIFICATIONS is seeded as "false" for everyone, so its
    // mere presence says nothing about whether the user was asked).
    const cordova = useCordova()
    const acceptPushNotifications = useSelector(selectAcceptPushNotifications)
    const pushPermissionPrompt = usePushPermissionPrompt()
    const injectPushPermissionPrompt = page === "home" && isSettled(cordova.status) && !acceptPushNotifications

    const { response, refresh } = useQueryWithOptions(() => getUserPrompts(page, token, currentEnv), {
        dependencies: [page, token, currentEnv],
        shouldGet: Boolean(token),
        errorDisplay: ErrorDisplay.None,
    })

    const { current, enqueue, silence } = useUserPrompts()
    const logger = new Logger("user-prompt")

    useInterval(() => {
        logger.info("Refreshing user prompts from API")
        refresh()
    }, Duration.fromObject({ minutes: pollIntervalInMinutes}).as("milliseconds"))

    useEffect(() => {
        if (response.loading) return

        // Enqueue replaces the queue, so the startup prompt goes in front of whatever the API returned
        // (a failed call just means no API prompts). Skip an API copy of the same prompt if it ever shows up.
        const apiPrompts = response.failed ? [] : response.response.data.prompts
        const prompts = injectPushPermissionPrompt
            ? [pushPermissionPrompt, ...apiPrompts.filter((p) => !("id" in p) || p.id !== pushPermissionPrompt.id)]
            : apiPrompts

        if (prompts.length > 0) enqueue(prompts)
    }, [response, enqueue, injectPushPermissionPrompt, pushPermissionPrompt])

    const nextPrompt = (current && current.type === UserPromptType.MODAL) ? current : undefined

    return (
        <>
            {nextPrompt && (
                <ModalUserPrompt
                    prompt={nextPrompt}
                    open={Boolean(nextPrompt)}
                    onClose={() => silence(nextPrompt)}
                />
            )}
        </>
    )
}
